import React from 'react';
import { format } from 'sql-formatter';
import { Highlight, themes, type Token, type RenderProps } from 'prism-react-renderer';
import { Copy } from 'lucide-react';
import Button from '@components/common/Button';
import { showToast } from '@utils/toast';
import styles from './SqlView.module.css';

interface SqlViewProps {
  /**
   * Raw SQL (params already inlined by the backend). A single logical query
   * execution can issue more than one statement (e.g. a multi-entity selection
   * with nested relations) — the backend joins them with `-- Query N of M`
   * markers, which this component splits back out so each statement is
   * formatted and highlighted on its own.
   */
  sql: string;
  /** Panel title. Defaults to "Executed SQL". */
  title?: string;
  /** Optional inline error banner (e.g. the SQL could not be saved). */
  error?: string | null;
}

const QUERY_MARKER = /^-- Query \d+ of \d+$/;

/** Splits the backend's joined SQL back into individual statements for separate formatting. */
function splitStatements(sql: string): string[] {
  const blocks = sql.split(/\n\n(?=-- Query \d+ of \d+)/);
  return blocks.map((block) => {
    const lines = block.split('\n');
    return QUERY_MARKER.test(lines[0]) ? lines.slice(1).join('\n') : block;
  });
}

/** Formats one statement; falls back to the raw text if sql-formatter can't parse it. */
function formatStatement(sql: string): string {
  try {
    return format(sql, { language: 'postgresql' });
  } catch {
    return sql;
  }
}

/** Renders one highlighted source line as its own component to keep JSX nesting shallow. */
const CodeLine: React.FC<{
  line: Token[];
  getLineProps: RenderProps['getLineProps'];
  getTokenProps: RenderProps['getTokenProps'];
}> = ({ line, getLineProps, getTokenProps }) => (
  <div {...getLineProps({ line })}>
    {line.map((token, tokenIndex) => (
      <span key={`${token.content}-${tokenIndex}`} {...getTokenProps({ token })} />
    ))}
  </div>
);

/** Highlight's render-prop body, extracted to its own component to keep JSX nesting shallow. */
const HighlightedCode: React.FC<{
  className: string;
  style: React.CSSProperties;
  tokens: Token[][];
  getLineProps: RenderProps['getLineProps'];
  getTokenProps: RenderProps['getTokenProps'];
}> = ({ className, style, tokens, getLineProps, getTokenProps }) => (
  <pre className={`${styles.code} ${className}`} style={style}>
    {tokens.map((line, lineIndex) => (
      <CodeLine
        key={`${lineIndex}-${line.map((t) => t.content).join('')}`}
        line={line}
        getLineProps={getLineProps}
        getTokenProps={getTokenProps}
      />
    ))}
  </pre>
);

/**
 * Structured, keyword-highlighted, read-only SQL viewer with copy-to-clipboard.
 * `sql-formatter` handles structure (line breaks/indent); `prism-react-renderer`
 * handles highlighting. Copy uses the FORMATTED text so the clipboard matches
 * what the user sees.
 */
const SqlView: React.FC<SqlViewProps> = ({ sql, title = 'Executed SQL', error }) => {
  const statements = splitStatements(sql).map(formatStatement);

  return (
    <div className={styles.panel} data-test-id="query-executed-sql">
      <div className={styles.header}>
        <span>{title}</span>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => {
            void navigator.clipboard.writeText(statements.join('\n\n'));
            showToast.success('SQL copied to clipboard');
          }}
        >
          <Copy size={14} /> Copy
        </Button>
      </div>

      {error && (
        <div className={styles.errorBanner} role="alert">
          {error}
        </div>
      )}

      {statements.map((statement, i) => (
        <React.Fragment key={`${i}-${statement.slice(0, 32)}`}>
          {statements.length > 1 && (
            <div className={styles.statementLabel}>
              Query {i + 1} of {statements.length}
            </div>
          )}
          <Highlight theme={themes.github} code={statement} language="sql">
            {({ className, style, tokens, getLineProps, getTokenProps }) => (
              <HighlightedCode
                className={className}
                style={style}
                tokens={tokens}
                getLineProps={getLineProps}
                getTokenProps={getTokenProps}
              />
            )}
          </Highlight>
        </React.Fragment>
      ))}
    </div>
  );
};

export default SqlView;
